import styled from 'styled-components';

import { Text } from '@/nova/components';
import { COLORS, ANIM, SPACE } from '@/nova/theme/tokens';

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

/* Matches the Select/TextArea field label so the promotion picker sits flush with the other controls. */
export const Label = styled(Text)`
  font-size: 14px;
  color: ${COLORS.text};
`;

/* The selected promotions, wrapping to as many rows as needed. Order matters: the first is the key
   visual (F0), so each chip carries its 1-based order badge. */
export const Chips = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

/* As tall as the rail's other controls (the 48px selects and buttons), so it is easy to tap on a phone. */
export const Chip = styled.span`
  display: flex;
  width: 100%;
  min-height: ${SPACE[48]};
  box-sizing: border-box;
  align-items: center;
  gap: ${SPACE[8]};
  padding: ${SPACE[4]} ${SPACE[4]} ${SPACE[4]} ${SPACE[12]};
  border-radius: 999px;
  background: ${COLORS.surfaceFaint};
  border: 1.5px solid ${COLORS.hairline};
`;

/* 1-based play-order badge; every position reads the same quiet outline (no key-visual highlight). */
export const OrderBadge = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${SPACE[20]};
  height: ${SPACE[20]};
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: ${COLORS.textMuted};
  background: ${COLORS.surfaceInset};
`;

export const Thumb = styled.span`
  flex: 0 0 auto;
  width: ${SPACE[32]};
  height: ${SPACE[32]};
  border-radius: 8px;
  overflow: hidden;
  background: ${COLORS.surfaceInset};
`;

export const ThumbImg = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const ChipLabel = styled(Text).attrs({ tag: 'span' as const })`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.heading};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RemoveBtn = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* A thumb-sized target inside the 48px chip. */
  width: ${SPACE[36]};
  height: ${SPACE[36]};
  padding: 0;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  color: ${COLORS.textMuted};
  background: transparent;
  transition:
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover:not(:disabled) {
    color: ${COLORS.heading};
    background: ${COLORS.surfaceInset};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

/* The "Choose promotions" trigger: a full-width dashed button so it reads as an add affordance, not a
   primary CTA (that is Generate). */
export const ChooseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACE[8]};
  width: 100%;
  min-height: ${SPACE[48]};
  padding: ${SPACE[8]} ${SPACE[12]};
  border-radius: 12px;
  border: 1.5px dashed ${COLORS.hairline};
  cursor: pointer;
  font-size: 14px;
  font-weight: 650;
  color: ${COLORS.ctaText};
  background: transparent;
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover:not(:disabled) {
    border-color: ${COLORS.ctaBorder};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const ChooseLabel = styled(Text).attrs({ tag: 'span' as const })``;
